import React, { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { SearchSelect } from '@/components/po/SearchSelect';
import { NO_SORT, defaultSort, sortChoices } from '@/components/po/sortChoices';
import { fgText, fmtQty, prApi } from '@/services/prApi';

/** Sort no for one picked FG — the supplier's default sort no for that FG is filled in; its supplier design name goes on the PO line. */
const SortForFg = ({ item, sorts, value, onChange }) => {
  const s = sorts.find((x) => x.sort_no === value);
  return (
    <div className="ml-8 mt-1.5 grid grid-cols-1 gap-2 rounded-md border border-indigo-100 bg-white p-2 md:grid-cols-2" data-testid={`pr-pick-sort-box-${item.pr_item_id}`}>
      <div><label className="text-[11px] text-gray-500">Sort No * <span className="text-gray-400">(supplier's sort nos for {item.mapped_fg_name || 'this item'} first)</span></label>
        <SearchSelect value={value || ''} options={sortChoices(sorts, item.mapped_fg_name)} onChange={onChange} placeholder="Pick the sort no"
          searchPlaceholder="Search sort no, supplier design, FG…" empty="No sort no for this supplier" testId={`pr-pick-sort-${item.pr_item_id}`}
          className={value ? '' : 'border-amber-400 bg-amber-50'} /></div>
      <div className="text-xs"><p className="text-[11px] text-gray-500">Supplier design name</p>
        <p className="mt-2 font-medium text-gray-800" data-testid={`pr-pick-supplier-design-${item.pr_item_id}`}>
          {value === NO_SORT ? <span className="italic text-gray-500">{item.design_name} (type it on the line)</span>
            : s ? (s.supplier_design_name || <span className="italic text-amber-700">not in Greige Sort Nos — {item.design_name} is used</span>) : '—'}</p></div>
    </div>
  );
};

/** Pick open requisition lines (still to order) of the PO's section; qty defaults to what is still to order, then the sort no per FG. */
export const PrLinePicker = ({ open, sectionName, prId, excludeIds = [], sortOptions = [], onClose, onPick }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState({});
  const [sorts, setSorts] = useState({});
  const [onlyPr, setOnlyPr] = useState(true);
  const prFilter = prId && onlyPr ? prId : undefined;
  useEffect(() => {
    if (!open) return undefined;
    setItems(null);
    const t = setTimeout(() => prApi.openItems({ section_name: sectionName, pr_id: prFilter, q: q.trim() || undefined }).then(setItems).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [open, sectionName, prFilter, q]);
  useEffect(() => { if (!open) { setSel({}); setSorts({}); setQ(''); setOnlyPr(true); } }, [open]);
  const rows = (items || []).filter((i) => !excludeIds.includes(i.pr_item_id));
  const picked = rows.filter((i) => sel[i.pr_item_id] !== undefined);
  const qtyOk = picked.every((i) => Number(sel[i.pr_item_id]) > 0 && Number(sel[i.pr_item_id]) <= i.balance_qty);
  const sortOk = picked.every((i) => sorts[i.pr_item_id]);
  const toggle = (i, on) => {
    setSel((s) => { const n = { ...s }; if (on) n[i.pr_item_id] = String(i.balance_qty); else delete n[i.pr_item_id]; return n; });
    if (on && !sorts[i.pr_item_id]) { const d = defaultSort(sortOptions, i.mapped_fg_name); if (d) setSorts((s) => ({ ...s, [i.pr_item_id]: d.sort_no })); }
  };
  const add = () => onPick(picked.map((i) => ({ item: i, qty: Number(sel[i.pr_item_id]), sort: sortOptions.find((s) => s.sort_no === sorts[i.pr_item_id]) || null })));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="pr-picker-dialog">
        <DialogHeader><DialogTitle>Add from requisition</DialogTitle>
          <DialogDescription>Open requisition lines of {sectionName} — pick the FG, its qty (not more than what is still to order) and the supplier's sort no.</DialogDescription></DialogHeader>
        <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search requisition no, FG, design, shade" className="pl-9" data-testid="pr-picker-search" /></div>
        {prId && <button type="button" onClick={() => setOnlyPr((v) => !v)} className="self-start text-xs text-indigo-600 hover:underline" data-testid="pr-picker-scope-toggle">
          {onlyPr ? `Showing one requisition — show all open requisitions of ${sectionName}` : 'Show only the requisition I came from'}</button>}
        <div className="max-h-[55vh] divide-y overflow-y-auto rounded-md border" data-testid="pr-picker-list">
          {!items && <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-indigo-600" />}
          {items && !rows.length && <p className="py-8 text-center text-sm text-gray-500" data-testid="pr-picker-empty">
            {items.length ? 'Every open line shown here is already on this PO' : `No open requisition lines for ${sectionName} — post a requisition first`}</p>}
          {rows.map((i) => {
            const on = sel[i.pr_item_id] !== undefined;
            return (
              <div key={i.pr_item_id} className={`px-3 py-2 ${on ? 'bg-indigo-50/60' : ''}`} data-testid={`pr-pick-row-${i.pr_item_id}`}>
                <div className="flex items-center gap-3">
                  <Checkbox checked={on} onCheckedChange={(v) => toggle(i, !!v)} data-testid={`pr-pick-check-${i.pr_item_id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="break-words text-sm font-medium">{i.mapped_fg_name && <span className="mr-1 font-mono">{i.mapped_fg_name}</span>}{fgText(i) || i.design_name}</div>
                    <div className="truncate text-xs text-gray-500"><span className="font-mono">{i.pr_number}</span>{i.required_by_date ? ` · needed by ${i.required_by_date}` : ''}
                      {i.fg_tax_pct != null ? ` · GST ${i.fg_tax_pct}%` : ''}</div>
                  </div>
                  <div className="whitespace-nowrap text-right text-xs text-gray-600">{fmtQty(i.required_qty)} {i.unit} asked<div className="font-semibold text-amber-700">{fmtQty(i.balance_qty)} to order</div></div>
                  <Input type="number" min="0" max={i.balance_qty} disabled={!on} value={on ? sel[i.pr_item_id] : ''} onChange={(e) => setSel((s) => ({ ...s, [i.pr_item_id]: e.target.value }))}
                    className={`h-8 w-24 ${on && !(Number(sel[i.pr_item_id]) > 0 && Number(sel[i.pr_item_id]) <= i.balance_qty) ? 'border-rose-400' : ''}`} data-testid={`pr-pick-qty-${i.pr_item_id}`} />
                </div>
                {on && <SortForFg item={i} sorts={sortOptions} value={sorts[i.pr_item_id]} onChange={(v) => setSorts((s) => ({ ...s, [i.pr_item_id]: v }))} />}
              </div>);
          })}
        </div>
        <DialogFooter className="items-center gap-2">
          {picked.length > 0 && !sortOk && <span className="mr-auto text-xs text-amber-700" data-testid="pr-picker-sort-missing">Pick the sort no of every FG you ticked</span>}
          <Button variant="outline" onClick={onClose} data-testid="pr-picker-cancel">Cancel</Button>
          <Button disabled={!picked.length || !qtyOk || !sortOk} onClick={add} data-testid="pr-picker-add">
            Add {picked.length || ''} line{picked.length === 1 ? '' : 's'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrLinePicker;
